import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardActions from '@mui/material/CardActions';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Chip from '@mui/material/Chip';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import QuestionAnswerIcon from '@mui/icons-material/QuestionAnswer';
import BuildIcon from '@mui/icons-material/Build';
import CaseIcon from '@mui/icons-material/BusinessCenter';
import TextField from '@mui/material/TextField';
import CircularProgress from '@mui/material/CircularProgress';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';

const Home = () => {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('lg'));
  
  // Newsletter state
  const [email, setEmail] = useState('');
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  const [loading, setLoading] = useState(false);
  const [subscribeResult, setSubscribeResult] = useState(null);

  const handleEmailChange = (e) => {
    setEmail(e.target.value);
  };

  const handleSubscribe = async () => {
    // Email validation regex
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    setSubscribeResult(null);

    if (!email) {
      const msg = 'Please enter your email address';
      setSubscribeResult({ success: false, message: msg });
      setSnackbarMessage(msg);
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      return;
    }

    if (!emailRegex.test(email)) {
      const msg = 'Please enter a valid email address';
      setSubscribeResult({ success: false, message: msg });
      setSnackbarMessage(msg);
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      return;
    }

    setLoading(true);
    const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
    const timeoutId = controller ? setTimeout(() => controller.abort(), 70000) : null;

    try {
      const response = await fetch("https://scrumtherapistserver.onrender.com/api/newsletter/subscribe", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email }),
          signal: controller ? controller.signal : undefined
      });

      let data = null;
      const text = await response.text().catch(() => '');
      try {
        data = text ? JSON.parse(text) : null;
      } catch {
        data = null;
      }

      if (!data || typeof data.success !== 'boolean' || !data.message) {
        data = {
          success: response.ok,
          message: response.ok
            ? 'Thank you for subscribing to our newsletter!'
            : 'Failed to subscribe. Please try again later.'
        };
      }

      setSubscribeResult({
        success: data.success,
        message: data.message,
        email,
        emailSent: data.emailSent === true
      });
      setSnackbarMessage(data.message);
      setSnackbarSeverity(data.success ? 'success' : 'error');

      if (data.success) {
        setEmail('');
        setTimeout(() => {
          const el = document.getElementById('newsletter-section');
          if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, 50);
      }
    } catch (error) {
      console.error('Error subscribing to newsletter:', error);
      const timedOut = error && error.name === 'AbortError';
      const msg = timedOut
        ? 'Request timed out. Please wait a moment and try again (the server may need a moment to start).'
        : 'Failed to subscribe. Please try again later.';
      setSubscribeResult({ success: false, message: msg, email });
      setSnackbarMessage(msg);
      setSnackbarSeverity('error');
    } finally {
      if (timeoutId) clearTimeout(timeoutId);
      setSnackbarOpen(true);
      setLoading(false);
    }
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };
  
  const features = [
    {
      title: 'Problem-Solving Q&A',
      description: 'Get clear, AI-powered answers to your Scrum questions, with examples you can use in your next sprint.',
      link: '/qanda',
      tag: 'Most Popular',
      icon: <QuestionAnswerIcon sx={{ fontSize: 40, color: 'primary.main' }} />,
      bullets: [
        'AI answers in plain language',
        'Scrum-focused, not generic advice',
        'Examples you can apply right away',
        'Context-aware suggestions for your team',
      ],
    },
    {
      title: 'Interactive Tools & Templates',
      description: 'Use guided tools for standups, retros, planning, and more, all tailored to your team.',
      link: '/tools',
      tag: 'Hands-on',
      icon: <BuildIcon sx={{ fontSize: 40, color: 'primary.main' }} />,
      bullets: [
        'Ready-to-use Scrum templates',
        'Guided facilitation for key ceremonies',
        'Configurable fields for your workflow',
        'Exportable outputs to share with the team',
      ],
    },
    {
      title: 'Real-World Scenarios',
      description: 'Practice with real-world Scrum scenarios and apply proven solutions to your own team.',
      link: '/case-studies',
      tag: 'Real cases',
      icon: <CaseIcon sx={{ fontSize: 40, color: 'primary.main' }} />,
      bullets: [
        'Scenarios based on real team challenges',
        'Step-by-step breakdown of what worked',
        'Common pitfalls and how to avoid them',
        'Talking points you can use with stakeholders',
      ],
    },
  ];

  return (
    <>
      <Box component="header" className="hero-section">
        <Container maxWidth="lg">
          <Box sx={{ 
            my: { xs: 4, md: 6, lg: 8 }, 
            textAlign: 'center',
            className: "hero-content"
          }}>
            <Typography 
              variant="h2" 
              component="h1" 
              gutterBottom 
              fontWeight="bold"
              sx={{ 
                fontSize: { xs: '2rem', md: '2.5rem', lg: '3rem' } 
              }}
            >
              Welcome to ScrumTherapist
            </Typography>
            <Typography 
              variant="h5" 
              paragraph 
              sx={{ 
                maxWidth: { xs: '100%', md: '800px' }, 
                mx: 'auto', 
                opacity: 0.9,
                fontSize: { xs: '1.1rem', md: '1.25rem', lg: '1.4rem' }
              }}
            >
              Your 24/7 consultant for all Scrum-related questions and challenges
            </Typography>
            <Button 
              variant="contained" 
              color="secondary" 
              size={isDesktop ? "large" : "medium"} 
              component={Link} 
              to="/qanda"
              sx={{ 
                mt: { xs: 2, md: 3 }, 
                px: { xs: 3, md: 4 }, 
                py: { xs: 1, md: 1.5 }, 
                borderRadius: 3,
                boxShadow: '0 4px 14px rgba(0, 0, 0, 0.25)',
                fontSize: { xs: '0.9rem', md: '1rem', lg: '1.1rem' }
              }}
              endIcon={<ArrowForwardIcon />}
            >
              Ask a Question
            </Button>
          </Box>
        </Container>
      </Box>

      <Container maxWidth="lg" component="section">
        <Box
          sx={{
            mt: { xs: 4, md: 6 },
            mb: { xs: 6, md: 8, lg: 10 },
            px: { xs: 2, md: 3 },
            py: { xs: 3, md: 5 },
            borderRadius: 3,
            backgroundColor: 'rgba(4, 57, 71, 0.03)',
          }}
        >
          <Grid
            container
            spacing={{ xs: 3, md: 4, lg: 5 }}
            sx={{
              display: 'flex',
              alignItems: 'stretch',
              justifyContent: 'center',
            }}
          >
            {features.map((feature, index) => (
              <Grid item key={index} xs={12} sx={{ display: 'flex', justifyContent: 'center' }}>
                <Card
                  className="feature-card"
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    height: '100%',
                    justifyContent: 'space-between',
                    flexGrow: 0,
                    width: '100%',
                    maxWidth: 900,
                    p: { xs: 2, md: 3, lg: 4 },
                    transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                    boxShadow: '0 4px 12px rgba(15, 23, 42, 0.06)',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: '0 12px 28px rgba(15, 23, 42, 0.12)',
                    },
                    '&:hover .feature-icon': {
                      transform: 'scale(1.08)',
                    },
                  }}
                >
                  <CardContent
                    sx={{
                      flexGrow: 1,
                      p: { xs: 2, md: 3, lg: 4 },
                      maxWidth: 900,
                      mx: 'auto',
                    }}
                  >
                    <Grid
                      container
                      spacing={{ xs: 2, md: 3 }}
                      alignItems="flex-start"
                    >
                      <Grid item xs={12} sm={6}>
                        {feature.tag && (
                          <Chip
                            label={feature.tag}
                            size="small"
                            sx={{
                              mb: 1.5,
                              fontSize: '0.7rem',
                              fontWeight: 600,
                              letterSpacing: 0.6,
                              textTransform: 'uppercase',
                              borderRadius: '999px',
                              backgroundColor: 'rgba(25, 118, 210, 0.06)',
                              borderColor: 'rgba(25, 118, 210, 0.3)',
                            }}
                            variant="outlined"
                            color="primary"
                          />
                        )}
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 1.5 }}>
                          {React.cloneElement(feature.icon, {
                            className: 'feature-icon',
                            sx: {
                              fontSize: { xs: 40, md: 50, lg: 60 },
                              color: 'primary.main',
                              mr: 1.5,
                              transition: 'transform 0.2s ease',
                            },
                          })}
                          <Typography
                            variant="h5"
                            component="h2"
                            fontWeight="bold"
                            sx={{
                              fontSize: {
                                xs: '1.25rem',
                                md: '1.4rem',
                                lg: '1.5rem',
                              },
                            }}
                          >
                            {feature.title}
                          </Typography>
                        </Box>
                        <Typography
                          variant="body1"
                          color="text.secondary"
                          sx={{
                            fontSize: {
                              xs: '0.95rem',
                              md: '1rem',
                              lg: '1.05rem',
                            },
                          }}
                        >
                          {feature.description}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Box
                          sx={{
                            borderRadius: 2,
                            border: '1px solid',
                            borderColor: 'divider',
                            p: { xs: 2, md: 2.5 },
                            backgroundColor: 'background.paper',
                          }}
                        >
                          <Typography
                            variant="subtitle1"
                            fontWeight="bold"
                            sx={{ mb: 1 }}
                          >
                            What you get
                          </Typography>
                          {feature.bullets.map((item, bulletIndex) => (
                            <Box
                              key={bulletIndex}
                              sx={{
                                display: 'flex',
                                alignItems: 'flex-start',
                                mb: bulletIndex === feature.bullets.length - 1 ? 0 : 0.75,
                              }}
                            >
                              <Box
                                sx={{
                                  width: 6,
                                  height: 6,
                                  borderRadius: '50%',
                                  bgcolor: 'primary.main',
                                  mt: 0.8,
                                  mr: 1.2,
                                }}
                              />
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                {item}
                              </Typography>
                            </Box>
                          ))}
                        </Box>
                      </Grid>
                    </Grid>
                  </CardContent>
                  <CardActions sx={{ justifyContent: 'center', pb: { xs: 2, md: 3 } }}>
                    <Button 
                      variant="outlined" 
                      color="primary" 
                      component={Link} 
                      to={feature.link}
                      endIcon={<ArrowForwardIcon />}
                      sx={{ 
                        fontSize: { xs: '0.9rem', md: '0.95rem', lg: '1rem' },
                        px: { xs: 2.5, md: 3.5 },
                        py: { xs: 0.8, md: 1 },
                        borderRadius: 999,
                        transition: 'box-shadow 0.2s ease, transform 0.2s ease, background-color 0.2s ease',
                        '&:hover': {
                          boxShadow: '0 8px 20px rgba(25, 118, 210, 0.35)',
                          transform: 'translateY(-1px)',
                          backgroundColor: 'rgba(25, 118, 210, 0.06)',
                        },
                      }}
                    >
                      Explore
                    </Button>
                  </CardActions>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>

        <Paper 
          component="section"
          elevation={0} 
          className="section" 
          sx={{ 
            mb: { xs: 4, md: 5, lg: 6 }, 
            py: { xs: 4, md: 6 },
            background: 'linear-gradient(135deg, #f5f7fa 0%, #e4e8f0 100%)' 
          }}
        >
          <Container maxWidth="lg">
            <Typography 
              variant="h4" 
              component="h2" 
              gutterBottom 
              align="center" 
              fontWeight="bold" 
              color="primary.dark"
              sx={{ fontSize: { xs: '1.5rem', md: '1.75rem', lg: '2rem' } }}
            >
              Who Can Benefit?
            </Typography>
            <Grid
              container
              spacing={{ xs: 2, md: 3, lg: 4 }}
              sx={{ mt: { xs: 1, md: 2 }, alignItems: 'stretch', justifyContent: 'center' }}
            >
              <Grid item xs={12} sm={6} md={3}>
                <Box
                  sx={{
                    width: '100%',
                    p: 2,
                    height: '100%',
                    borderRadius: 2,
                    bgcolor: 'white',
                    boxShadow: '0 2px 10px rgba(0,0,0,0.05)',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                    border: '1px solid rgba(25, 118, 210, 0.15)',
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: '50%',
                        backgroundColor: 'rgba(25, 118, 210, 0.08)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mr: 1.5,
                        fontSize: '1.2rem',
                      }}
                    >
                      SM
                    </Box>
                    <Typography variant="h6" gutterBottom color="primary.main" fontWeight="bold" sx={{ mb: 0 }}>
                      Scrum Masters
                    </Typography>
                  </Box>
                  <Typography
                    variant="subtitle2"
                    sx={{
                      fontWeight: 'bold',
                      mb: 0.5,
                    }}
                  >
                    Coach better, with ready-made answers to hard questions.
                  </Typography>
                  <Typography paragraph sx={{ fontSize: { xs: '0.95rem', md: '1rem', lg: '1.05rem' } }}>
                    Get guidance on facilitation, conflict resolution, and team dynamics so you can serve your team more effectively.
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Box
                  sx={{
                    width: '100%',
                    p: 2,
                    height: '100%',
                    borderRadius: 2,
                    bgcolor: 'white',
                    boxShadow: '0 2px 10px rgba(0,0,0,0.05)',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                    border: '1px solid rgba(0, 191, 165, 0.18)',
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: '50%',
                        backgroundColor: 'rgba(0, 191, 165, 0.08)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mr: 1.5,
                        fontSize: '1.2rem',
                      }}
                    >
                      PO
                    </Box>
                    <Typography variant="h6" gutterBottom color="primary.main" fontWeight="bold" sx={{ mb: 0 }}>
                      Product Owners
                    </Typography>
                  </Box>
                  <Typography
                    variant="subtitle2"
                    sx={{
                      fontWeight: 'bold',
                      mb: 0.5,
                    }}
                  >
                    Keep backlog and stakeholders aligned without long meetings.
                  </Typography>
                  <Typography paragraph sx={{ fontSize: { xs: '0.95rem', md: '1rem', lg: '1.05rem' } }}>
                    Learn practical techniques for prioritization, stakeholder communication, and value-focused roadmapping.
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Box
                  sx={{
                    width: '100%',
                    p: 2,
                    height: '100%',
                    borderRadius: 2,
                    bgcolor: 'white',
                    boxShadow: '0 2px 10px rgba(0,0,0,0.05)',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                    border: '1px solid rgba(156, 39, 176, 0.18)',
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: '50%',
                        backgroundColor: 'rgba(156, 39, 176, 0.08)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mr: 1.5,
                        fontSize: '1.2rem',
                      }}
                    >
                      DEV
                    </Box>
                    <Typography variant="h6" gutterBottom color="primary.main" fontWeight="bold" sx={{ mb: 0 }}>
                      Development Team Members
                    </Typography>
                  </Box>
                  <Typography
                    variant="subtitle2"
                    sx={{
                      fontWeight: 'bold',
                      mb: 0.5,
                    }}
                  >
                    Collaborate better and deliver increments you can be proud of.
                  </Typography>
                  <Typography paragraph sx={{ fontSize: { xs: '0.95rem', md: '1rem', lg: '1.05rem' } }}>
                    Discover best practices for communication, technical excellence, and self-organization inside your Scrum team.
                  </Typography>

                </Box>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Box
                  sx={{
                    width: '100%',
                    p: 2,
                    height: '100%',
                    borderRadius: 2,
                    bgcolor: 'white',
                    boxShadow: '0 2px 10px rgba(0,0,0,0.05)',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                    border: '1px solid rgba(255, 152, 0, 0.18)',
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: '50%',
                        backgroundColor: 'rgba(255, 152, 0, 0.08)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mr: 1.5,
                        fontSize: '1.2rem',
                      }}
                    >
                      NEW
                    </Box>
                    <Typography variant="h6" gutterBottom color="primary.main" fontWeight="bold" sx={{ mb: 0 }}>
                      Teams Transitioning to Scrum
                    </Typography>
                  </Box>
                  <Typography
                    variant="subtitle2"
                    sx={{
                      fontWeight: 'bold',
                      mb: 0.5,
                    }}
                  >
                    Make your Scrum adoption smoother and less risky.
                  </Typography>
                  <Typography paragraph sx={{ fontSize: { xs: '0.95rem', md: '1rem', lg: '1.05rem' } }}>
                    Find resources, talking points, and practical steps to help your team move from traditional delivery to Scrum.
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Container>
        </Paper>

        <Box component="section" sx={{ textAlign: 'center', mb: { xs: 6, md: 8, lg: 10 } }}>
          <Typography 
            variant="h4" 
            component="h2" 
            gutterBottom 
            fontWeight="bold" 
            color="primary.dark"
            sx={{ fontSize: { xs: '1.5rem', md: '1.75rem', lg: '2rem' } }}
          >
            Ready to Improve Your Scrum Practice?
          </Typography>
          <Typography 
            variant="body1" 
            paragraph 
            sx={{ 
              maxWidth: { xs: '100%', md: '800px', lg: '900px' }, 
              mx: 'auto',
              fontSize: { xs: '0.95rem', md: '1rem', lg: '1.1rem' }
            }}
          >
            ScrumTherapist provides the guidance, tools, and resources you need to overcome challenges and excel in your Agile journey.
          </Typography>
          <Button 
            variant="contained" 
            color="primary" 
            size={isDesktop ? "large" : "medium"}
            component={Link} 
            to="/tools"
            sx={{ 
              mt: { xs: 2, md: 3 }, 
              px: { xs: 3, md: 4 }, 
              py: { xs: 1, md: 1.5 }, 
              borderRadius: 3,
              boxShadow: '0 4px 14px rgba(0, 0, 0, 0.15)',
              fontSize: { xs: '0.9rem', md: '1rem', lg: '1.1rem' }
            }}
            endIcon={<ArrowForwardIcon />}
          >
            Explore Tools
          </Button>
          
          {/* Newsletter Section */}
          <Box
            id="newsletter-section"
            sx={{ 
              mt: 8, 
              p: 4, 
              borderRadius: 2, 
              backgroundColor: 'primary.dark',
              color: 'white',
              maxWidth: '800px',
              mx: 'auto',
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)'
            }}
          >
            <Typography 
              variant="h5" 
              component="h3" 
              gutterBottom 
              fontWeight="bold"
              sx={{ color: 'primary.light' }}
            >
              Subscribe to Our Newsletter
            </Typography>
            <Typography 
              variant="body1" 
              sx={{ mb: 3, color: 'grey.300' }}
            >
              Get the latest Scrum tips, tools, and resources delivered directly to your inbox.
            </Typography>

            {subscribeResult && subscribeResult.success && (
              <Box
                sx={{
                  mb: 3,
                  p: 2,
                  borderRadius: 1.5,
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 1.5,
                  bgcolor: 'success.light',
                  color: 'success.dark',
                  border: 1,
                  borderColor: 'success.main'
                }}
                role="status"
                aria-live="polite"
              >
                <CheckCircleOutlineIcon sx={{ mt: 0.25 }} />
                <Box>
                  <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 0.5 }}>
                    You're subscribed! 🎉
                  </Typography>
                  <Typography variant="body2">
                    {subscribeResult.message}
                    {subscribeResult.emailSent && subscribeResult.email && (
                      <> Confirmation sent to <strong>{subscribeResult.email}</strong> (check your spam folder if needed).</>
                    )}
                    {!subscribeResult.emailSent && subscribeResult.email && (
                      <> We've added <strong>{subscribeResult.email}</strong> to our list.</>
                    )}
                  </Typography>
                </Box>
              </Box>
            )}

            {subscribeResult && !subscribeResult.success && (
              <Box
                sx={{
                  mb: 3,
                  p: 2,
                  borderRadius: 1.5,
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 1.5,
                  bgcolor: 'rgba(211, 47, 47, 0.15)',
                  color: 'error.light',
                  border: 1,
                  borderColor: 'error.main'
                }}
                role="alert"
                aria-live="assertive"
              >
                <ErrorOutlineIcon sx={{ mt: 0.25 }} />
                <Box>
                  <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 0.5 }}>
                    Subscription failed
                  </Typography>
                  <Typography variant="body2">
                    {subscribeResult.message}
                    {subscribeResult.email && <span> (<strong>{subscribeResult.email}</strong>)</span>}
                  </Typography>
                </Box>
              </Box>
            )}

            <Box sx={{ 
              display: 'flex', 
              flexDirection: { xs: 'column', md: 'row' }, 
              gap: 2,
              maxWidth: '600px',
              mx: 'auto'
            }}>
              <TextField
                size="medium"
                placeholder="Your email address"
                variant="outlined"
                fullWidth
                value={email}
                onChange={handleEmailChange}
                disabled={loading}
                sx={{
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  borderRadius: 1,
                  '& .MuiOutlinedInput-root': {
                    '& fieldset': {
                      borderColor: 'rgba(255, 255, 255, 0.3)',
                    },
                    '&:hover fieldset': {
                      borderColor: 'primary.light',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: 'primary.light',
                    },
                  },
                  '& .MuiInputBase-input': {
                    color: 'white',
                  },
                }}
                InputProps={{
                  startAdornment: (
                    <MailOutlineIcon sx={{ color: 'grey.400', mr: 1 }} />
                  ),
                }}
              />
              <Button
                variant="contained"
                color="secondary"
                onClick={handleSubscribe}
                disabled={loading}
                sx={{
                  whiteSpace: 'nowrap',
                  minWidth: { xs: '100%', md: 'auto' },
                  px: 3,
                  py: 1.5
                }}
              >
                {loading ? <CircularProgress size={24} color="inherit" /> : 'Subscribe'}
              </Button>
            </Box>
            <Typography variant="caption" sx={{ mt: 2, display: 'block', color: 'grey.400' }}>
              We respect your privacy. Unsubscribe at any time.
            </Typography>
          </Box>
        </Box>
      </Container>
      
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={snackbarSeverity === 'success' ? 10000 : 6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        sx={{ zIndex: (t) => (t.zIndex?.modal || 1400) + 100 }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbarSeverity}
          sx={{
            width: '100%',
            minWidth: { xs: '92vw', sm: 480 },
            maxWidth: 720,
            boxShadow: 6,
            border: 1,
            borderColor: `${snackbarSeverity}.main`,
            fontSize: '1rem',
            fontWeight: snackbarSeverity === 'success' ? 'bold' : 'medium',
            bgcolor: snackbarSeverity === 'success' ? 'success.light' : undefined,
            color: snackbarSeverity === 'success' ? 'success.dark' : undefined
          }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </>
  );
};

export default Home;
